/**
 * Tracking GA4 performant et centralisé pour Emplois BNSSA & MNS.
 *
 * Principes :
 * - Aucune PII (pas d'email, téléphone, nom complet) n'est envoyée à GA4.
 * - Aucune dépendance directe à `window.gtag` côté composants : on passe par
 *   les helpers `track*` ci-dessous, qui restent silencieux si GA n'est pas chargé.
 * - Performance : exécution non-bloquante via `requestIdleCallback` (fallback
 *   `setTimeout`) + queue tampon en attendant que `window.gtag` soit prêt.
 * - Robustesse : tout échec est silencieux (try/catch) pour ne JAMAIS casser
 *   l'UX, y compris en cas de bloqueur de pub.
 * - Anti-doublon : déduplication courte fenêtre (1500ms) sur la signature
 *   `event + key params` pour éviter le sur-comptage (double click, re-renders).
 *
 * Convention de nommage des événements GA4 :
 *   - snake_case
 *   - regroupés par domaine fonctionnel (job, alert, candidate, account, ...)
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const GA_MEASUREMENT_ID = "G-F09FR0N66Z";

/** Délai (ms) en dessous duquel un même évènement+clé ne sera pas rejoué. */
const DEDUPE_WINDOW_MS = 1500;

/** Taille max de la queue tampon (avant que gtag soit chargé). */
const QUEUE_MAX = 50;

type Primitive = string | number | boolean | null | undefined;
type EventParams = Record<string, Primitive>;

interface QueuedEvent {
  name: string;
  params: EventParams;
  ts: number;
}

const queue: QueuedEvent[] = [];
const recentEvents = new Map<string, number>();
let flushScheduled = false;

/** Programme une tâche en idle, fallback setTimeout. */
function scheduleIdle(cb: () => void): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    try {
      w.requestIdleCallback(cb, { timeout: 500 });
      return;
    } catch {
      // fallback ci-dessous
    }
  }
  setTimeout(cb, 0);
}

/** Vrai si gtag est utilisable. */
function isGtagReady(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/** Vide la queue dès que gtag est prêt. */
function flushQueue(): void {
  if (!isGtagReady()) return;
  while (queue.length > 0) {
    const ev = queue.shift();
    if (!ev) break;
    try {
      window.gtag!("event", ev.name, ev.params);
    } catch {
      // silencieux
    }
  }
  flushScheduled = false;
}

/** Tente de flusher la queue ; retente plus tard si gtag pas prêt (max 5 essais). */
function scheduleFlush(attempt = 0): void {
  if (flushScheduled) return;
  flushScheduled = true;
  scheduleIdle(() => {
    if (isGtagReady()) {
      flushQueue();
      return;
    }
    flushScheduled = false;
    if (attempt < 5) {
      setTimeout(() => scheduleFlush(attempt + 1), 400 * (attempt + 1));
    }
  });
}

/** Construit la clé de déduplication. */
function dedupeKey(name: string, params: EventParams): string {
  // On exclut les valeurs `undefined`/`null` pour éviter les clés instables.
  const parts = Object.keys(params)
    .sort()
    .map((k) => {
      const v = params[k];
      if (v === undefined || v === null) return "";
      return `${k}=${String(v)}`;
    })
    .filter(Boolean)
    .join("&");
  return `${name}|${parts}`;
}

/** Envoie un évènement GA4 de manière sûre, non-bloquante et dédupliquée. */
export function trackEvent(name: string, params: EventParams = {}): void {
  if (typeof window === "undefined") return;

  try {
    const now = Date.now();
    const key = dedupeKey(name, params);
    const last = recentEvents.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) {
      return; // doublon récent
    }
    recentEvents.set(key, now);

    // Nettoyage opportuniste de la map (évite la fuite mémoire en SPA longue durée)
    if (recentEvents.size > 200) {
      const cutoff = now - DEDUPE_WINDOW_MS * 4;
      for (const [k, ts] of recentEvents) {
        if (ts < cutoff) recentEvents.delete(k);
      }
    }

    const enriched: EventParams = {
      ...params,
      send_to: GA_MEASUREMENT_ID,
    };

    if (isGtagReady()) {
      scheduleIdle(() => {
        try {
          window.gtag!("event", name, enriched);
        } catch {
          // silencieux
        }
      });
      return;
    }

    // gtag pas encore prêt : on met en queue
    if (queue.length >= QUEUE_MAX) queue.shift();
    queue.push({ name, params: enriched, ts: now });
    scheduleFlush();
  } catch {
    // silencieux : le tracking ne doit jamais casser l'app
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Helpers métier (API stable utilisée par les composants)
 * ────────────────────────────────────────────────────────────────────── */

export type JobCategoryParam = "BNSSA" | "MNS" | "ALL" | string;

/** Vue d'une fiche offre (pour mesurer le funnel candidature). */
export function trackJobView(p: {
  jobId: string;
  category?: JobCategoryParam;
  contractType?: string;
  region?: string;
  source?: "static" | "recruiter";
}): void {
  trackEvent("job_view", {
    job_id: p.jobId,
    job_category: p.category,
    contract_type: p.contractType,
    region: p.region,
    job_source: p.source,
  });
}

/** Démarrage d'une candidature (ouverture/init du formulaire). */
export function trackJobApplicationStart(p: {
  jobId: string;
  category?: JobCategoryParam;
  contractType?: string;
}): void {
  trackEvent("job_application_start", {
    job_id: p.jobId,
    job_category: p.category,
    contract_type: p.contractType,
  });
}

/** Candidature envoyée avec succès. */
export function trackJobApplicationSubmit(p: {
  jobId: string;
  category?: JobCategoryParam;
  contractType?: string;
  isAE?: boolean;
}): void {
  trackEvent("job_application_submit", {
    job_id: p.jobId,
    job_category: p.category,
    contract_type: p.contractType,
    is_auto_entrepreneur: p.isAE ? 1 : 0,
  });
}

/** Création (ou tentative guest) d'une alerte emploi candidat. */
export function trackJobAlertCreated(p: {
  category?: JobCategoryParam;
  region?: string;
  frequency?: string;
  asGuest?: boolean;
}): void {
  trackEvent("job_alert_created", {
    job_category: p.category,
    region: p.region,
    frequency: p.frequency,
    as_guest: p.asGuest ? 1 : 0,
  });
}

/** Création/mise à jour d'un profil "disponible" (CVthèque candidat). */
export function trackAvailabilityProfileSaved(p: {
  qualification?: string;
  region?: string;
  isAvailableNow?: boolean;
}): void {
  trackEvent("cvtheque_profile_saved", {
    qualification: p.qualification,
    region: p.region,
    is_available_now: p.isAvailableNow ? 1 : 0,
  });
}

/** Réactivation 48h d'un profil CVthèque. */
export function trackAvailabilityReactivated(p: { qualification?: string }): void {
  trackEvent("cvtheque_profile_reactivated", {
    qualification: p.qualification,
  });
}

/* ── Tunnel de dépôt de disponibilité candidat (CTA /disponibles) ───────── */

/** Point d'entrée du tunnel : ouverture de la pop-up de disponibilité. */
export function trackAvailabilityDepositOpen(p: { authenticated?: boolean } = {}): void {
  trackEvent("availability_deposit_open", {
    authenticated: p.authenticated ? 1 : 0,
  });
}

/** Soumission de la pop-up par un visiteur non connecté : brouillon sauvegardé. */
export function trackAvailabilityDepositDraftSaved(p: {
  qualification?: string;
  region?: string;
  isAvailableNow?: boolean;
}): void {
  trackEvent("availability_deposit_draft_saved", {
    qualification: p.qualification,
    region: p.region,
    is_available_now: p.isAvailableNow ? 1 : 0,
  });
}

/** Soumission de la pop-up par un candidat déjà connecté : publication directe. */
export function trackAvailabilityDepositPublishedDirect(p: {
  qualification?: string;
  region?: string;
  isAvailableNow?: boolean;
}): void {
  trackEvent("availability_deposit_published_direct", {
    qualification: p.qualification,
    region: p.region,
    is_available_now: p.isAvailableNow ? 1 : 0,
  });
}

/** Finalisation après inscription : le brouillon est consommé dans le dashboard. */
export function trackAvailabilityDepositDraftConsumed(p: {
  qualification?: string;
  region?: string;
  isAvailableNow?: boolean;
}): void {
  trackEvent("availability_deposit_draft_consumed", {
    qualification: p.qualification,
    region: p.region,
    is_available_now: p.isAvailableNow ? 1 : 0,
  });
}

/** Création d'une alerte candidat (côté recruteur). */
export function trackCandidateAlertCreated(p: {
  qualification?: string;
  region?: string;
  frequency?: string;
}): void {
  trackEvent("candidate_alert_created", {
    qualification: p.qualification,
    region: p.region,
    frequency: p.frequency,
  });
}

/** Inscription d'un compte (candidat / recruteur / organisme). */
export function trackSignUp(p: { role: string; method: "password" | "oauth_google" }): void {
  trackEvent("sign_up", {
    role: p.role,
    method: p.method,
  });
}

/** Connexion d'un compte. */
export function trackLogin(p: { role: string; method: "password" | "oauth_google" }): void {
  trackEvent("login", {
    role: p.role,
    method: p.method,
  });
}

/** Bascule d'un favori côté candidat. */
export function trackJobFavoriteToggle(p: { jobId: string; favorited: boolean }): void {
  trackEvent("job_favorite_toggle", {
    job_id: p.jobId,
    favorited: p.favorited ? 1 : 0,
  });
}

/** Création d'une offre par un recruteur. */
export function trackRecruiterJobCreated(p: {
  category?: JobCategoryParam;
  contractType?: string;
  bulk?: boolean;
}): void {
  trackEvent("recruiter_job_created", {
    job_category: p.category,
    contract_type: p.contractType,
    bulk: p.bulk ? 1 : 0,
  });
}

/* ── Tunnel de dépôt d'annonce recruteur (CTA hero home / /offres / header) ─ */

/** Point d'entrée du tunnel : ouverture de la pop-up de dépôt d'annonce. */
export function trackJobDepositOpen(p: {
  source?: "home_hero" | "jobs_hero" | "header" | string;
  authenticated?: boolean;
} = {}): void {
  trackEvent("job_deposit_open", {
    source: p.source,
    authenticated: p.authenticated ? 1 : 0,
  });
}

/** Soumission de la pop-up par un recruteur/admin connecté : publication directe. */
export function trackJobDepositPublishedDirect(p: {
  category?: JobCategoryParam;
  contractType?: string;
  region?: string;
}): void {
  trackEvent("job_deposit_published_direct", {
    job_category: p.category,
    contract_type: p.contractType,
    region: p.region,
  });
}

/** Soumission de la pop-up par un visiteur non connecté : brouillon sauvegardé. */
export function trackJobDepositDraftSaved(p: {
  category?: JobCategoryParam;
  contractType?: string;
  region?: string;
}): void {
  trackEvent("job_deposit_draft_saved", {
    job_category: p.category,
    contract_type: p.contractType,
    region: p.region,
  });
}

/* ── Tunnel de dépôt de formation organisme (CTA /formations) ────────────── */

/** Point d'entrée du tunnel : ouverture de la pop-up de dépôt de formation. */
export function trackFormationDepositOpen(p: {
  authenticated?: boolean;
} = {}): void {
  trackEvent("formation_deposit_open", {
    authenticated: p.authenticated ? 1 : 0,
  });
}

/** Soumission de la pop-up par un organisme/admin connecté : publication directe. */
export function trackFormationDepositPublishedDirect(p: {
  category?: JobCategoryParam;
  region?: string;
}): void {
  trackEvent("formation_deposit_published_direct", {
    job_category: p.category,
    region: p.region,
  });
}

/** Soumission de la pop-up par un visiteur non connecté : brouillon sauvegardé. */
export function trackFormationDepositDraftSaved(p: {
  category?: JobCategoryParam;
  region?: string;
}): void {
  trackEvent("formation_deposit_draft_saved", {
    job_category: p.category,
    region: p.region,
  });
}

/* ── Messagerie CVthèque (conversations candidat ↔ recruteur/organisme) ───── */

export type MessagingContextParam =
  | "cvtheque"
  | "job_ae"
  | "application"
  | "direct"
  | string;

/** Démarrage d'une conversation (nouvelle conversation créée). */
export function trackMessageConversationStarted(p: {
  context?: MessagingContextParam;
  initiatorRole?: string;
}): void {
  trackEvent("message_conversation_started", {
    conversation_context: p.context,
    initiator_role: p.initiatorRole,
  });
}

/** Envoi d'un message dans une conversation. */
export function trackMessageSent(p: {
  context?: MessagingContextParam;
  senderRole?: string;
  anonymityLifted?: boolean;
}): void {
  trackEvent("message_sent", {
    conversation_context: p.context,
    sender_role: p.senderRole,
    anonymity_lifted: p.anonymityLifted ? 1 : 0,
  });
}

/** Le candidat révèle son identité au recruteur (CVthèque). */
export function trackMessageIdentityRevealed(p: {
  context?: MessagingContextParam;
} = {}): void {
  trackEvent("message_identity_revealed", {
    conversation_context: p.context,
  });
}

/** Demande de suppression de compte (RGPD). */
export function trackAccountDeletionRequest(p: { role?: string }): void {
  trackEvent("account_deletion_request", {
    role: p.role,
  });
}

/** Clic sur un CTA générique (utile pour mesurer le ROI de boutons clés). */
export function trackCtaClick(p: { cta: string; location?: string }): void {
  trackEvent("cta_click", {
    cta: p.cta,
    cta_location: p.location,
  });
}
